// Hot Desking Phones — list of phones whose user signed in via *77*<ext>.
// Distinct from /hotdesk (which manages the slot inventory).
//
// Toolbar mirrors 3CX 144420: + Add | 🗑 Delete | ⏎ Logout

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, LogOut, Search } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function HotDeskingPhones() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const { data: rows = [] } = useQuery<any[]>({
    queryKey: ['hot-desking-phones'],
    queryFn: () => fJson('/api/hotdesk/sessions').catch(() => []),
    refetchInterval: 10_000,
  });

  const filtered = (rows as any[]).filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [r.extension, r.phone_label, r.device_mac].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  function togglePick(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }
  function togglePickAll() {
    if (picked.size > 0) setPicked(new Set());
    else setPicked(new Set(filtered.map((r: any) => r.session_id)));
  }

  const logout = useMutation({
    mutationFn: (id: string) => fJson(`/api/hotdesk/sessions/${id}/logout`, { method: 'POST' }),
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['hot-desking-phones'] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => fJson(`/api/hotdesk/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['hot-desking-phones'] }); },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/phones')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Phones</button>
        <h1 className="text-xl font-semibold">Hot Desking Phones</h1>
      </div>

      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => navigate('/hotdesk')}>
          <Plus size={14} /> Add
        </button>
        <button className="btn-ghost text-sm inline-flex items-center gap-1 disabled:opacity-40"
                onClick={() => picked.size && confirm(`Delete ${picked.size} session${picked.size === 1 ? '' : 's'}?`) && Array.from(picked).forEach(id => remove.mutate(id))}
                disabled={picked.size === 0}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-ghost text-sm inline-flex items-center gap-1 disabled:opacity-40"
                onClick={() => picked.size && Array.from(picked).forEach(id => logout.mutate(id))}
                disabled={picked.size === 0}>
          <LogOut size={14} /> Logout
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                 className="bg-transparent outline-none text-sm w-48" />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox" checked={picked.size > 0 && picked.size === filtered.length} onChange={togglePickAll} />
              </th>
              <th>Extension</th>
              <th>IP Phone</th>
              <th>MAC</th>
              <th>Logged In</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r: any) => (
              <tr key={r.session_id} className="hover:bg-elevated">
                <td><input type="checkbox" checked={picked.has(r.session_id)} onChange={() => togglePick(r.session_id)} /></td>
                <td className="font-mono">{r.extension || '—'}</td>
                <td>{r.phone_label || `${r.device_vendor || ''} ${r.device_model || ''}`.trim() || '—'}</td>
                <td className="font-mono text-xs">{r.device_mac || '—'}</td>
                <td className="text-xs text-textSecondary">{r.logged_in_at ? new Date(r.logged_in_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-textSecondary">
                No hot-desking sessions active. Users dial <code className="bg-elevated px-1 mx-1 rounded">*77*&lt;extension&gt;</code> from a free hot-desk slot to claim it.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
